import { useMemo, useState, useId } from 'react';
import api from '../API.js';

const LIGHT_EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * Reusable “create-or-return-existing” user creation component backed by
 * POST emailauth/users/passwordless.
 *
 * Props:
 * - onUserResolved?: (user, { created }) => void
 */
export default function EmailAuthCreateUser({ onUserResolved = null }) {
    const idPrefix = useId();
    const [email, setEmail] = useState('');
    const [login, setLogin] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [pending, setPending] = useState(false);
    const [error, setError] = useState('');
    const [loginError, setLoginError] = useState('');
    const [result, setResult] = useState(null); // { user, created }

    const emailTrimmed = email.trim();
    const emailFormatOk = useMemo(() => {
        if (!emailTrimmed) return true; // rely on required for empties
        return LIGHT_EMAIL_RE.test(emailTrimmed);
    }, [emailTrimmed]);

    async function submit(e) {
        e.preventDefault();
        setPending(true);
        setError('');
        setLoginError('');
        try {
            const loginTrimmed = login.trim();
            const resp = await api.post('emailauth/users/passwordless', {
                json: {
                    email: emailTrimmed,
                    ...(loginTrimmed ? { login: loginTrimmed } : {}),
                    firstName: firstName.trim(),
                    lastName: lastName.trim(),
                },
            });
            if (resp.status !== 200 || !resp.data?.user) {
                if (resp?.data?.type === 'validation' && resp?.data?.field === 'login' && resp?.data?.message) {
                    setLoginError(String(resp.data.message));
                    return;
                }
                setError('Could not create account. Contact your site administrator.');
                return;
            }
            const next = { user: resp.data.user, created: !!resp.data.created };
            setResult(next);
            if (typeof onUserResolved === 'function') {
                onUserResolved(next.user, { created: next.created });
            }
        } catch {
            setError('Could not create account. Contact your site administrator.');
        } finally {
            setPending(false);
        }
    }

    function reset() {
        setEmail('');
        setLogin('');
        setFirstName('');
        setLastName('');
        setError('');
        setLoginError('');
        setResult(null);
    }

    return (
        <div>
            <form onSubmit={submit} aria-busy={pending}>
                <div style={{ display: 'grid', gap: 10 }}>
                    <div style={{ display: 'grid', gap: 6 }}>
                        <label htmlFor={`${idPrefix}-email`}>Email</label>
                        <input
                            id={`${idPrefix}-email`}
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="name@example.org"
                            aria-invalid={!emailFormatOk}
                        />
                        {!emailFormatOk ? (
                            <div role="alert" style={{ fontSize: '0.9rem' }}>
                                Please enter a valid email address.
                            </div>
                        ) : null}
                    </div>
                    <div style={{ display: 'grid', gap: 6 }}>
                        <label htmlFor={`${idPrefix}-login`}>
                            Login <span style={{ opacity: 0.75 }}>(optional)</span>
                        </label>
                        <input
                            id={`${idPrefix}-login`}
                            type="text"
                            value={login}
                            onChange={(e) => setLogin(e.target.value)}
                            placeholder="Leave blank to auto-generate"
                            aria-invalid={!!loginError}
                        />
                        {loginError ? (
                            <div role="alert" style={{ fontSize: '0.9rem' }}>
                                {loginError}
                            </div>
                        ) : null}
                    </div>
                    <div style={{ display: 'grid', gap: 6 }}>
                        <label htmlFor={`${idPrefix}-first`}>First name</label>
                        <input
                            id={`${idPrefix}-first`}
                            type="text"
                            required
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                        />
                    </div>
                    <div style={{ display: 'grid', gap: 6 }}>
                        <label htmlFor={`${idPrefix}-last`}>Last name</label>
                        <input
                            id={`${idPrefix}-last`}
                            type="text"
                            required
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                        />
                    </div>
                </div>

                {error ? (
                    <div role="alert" style={{ marginTop: 10 }}>
                        {error}
                    </div>
                ) : null}

                <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
                    <button type="submit" disabled={pending || !emailFormatOk}>
                        {pending ? 'Creating…' : 'Create account'}
                    </button>
                    {result ? (
                        <button type="button" onClick={reset} disabled={pending}>
                            Create another
                        </button>
                    ) : null}
                </div>
            </form>

            {result?.user ? (
                <div style={{ marginTop: 14 }}>
                    {!result.created ? (
                        <div style={{ marginBottom: 8 }}>
                            That email already exists. You can use the existing user below.
                        </div>
                    ) : (
                        <div style={{ marginBottom: 8 }}>
                            Account created.
                        </div>
                    )}
                    <div style={{ padding: 10, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 8 }}>
                        <div><strong>Name:</strong> {result.user.firstName} {result.user.lastName}</div>
                        <div><strong>Email:</strong> {result.user.email}</div>
                        <div><strong>Login:</strong> {result.user.login}</div>
                        <div><strong>User ID:</strong> {result.user._id}</div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

